import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { QuickscanData, QuickscanResults } from '@/types/quickscan';

/**
 * Opslaan van een publieke quickscan.
 *
 * Dit deed vroeger zeven inserts rechtstreeks vanuit de browser, als `anon`.
 * Dat werkte sinds 23-08-2025 niet meer: op `quickscan_submissions` hing een
 * trigger die een lead aanmaakte zonder `SECURITY DEFINER`, dus met de rechten
 * van de bezoeker. `leads` eist een rol, de insert werd geweigerd, en omdat een
 * trigger in dezelfde transactie zit verdween de inzending mee. Ruim een jaar
 * lang kwam er niets binnen zonder dat iets een foutmelding gaf (F8.9).
 *
 * Alles loopt nu via de edge-functie `quickscan-submit`, die met de service-rol
 * schrijft. Twee stappen, bewust gescheiden:
 *
 *   opslaan          — de scan zelf; nog geen lead, want er is nog geen naam
 *   contactgegevens  — e-mail + bedrijf erbij, en dán pas contact, org en lead
 *
 * Schrijf hier nooit meer rechtstreeks naar een `quickscan_*`-tabel: `anon`
 * heeft daar geen rechten meer (migratie 046).
 */
export const useQuickscanSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  /** Roept de functie aan en maakt van elke faalvorm één begrijpelijke fout. */
  const roep = async (payload: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('quickscan-submit', {
      body: payload,
    });
    if (error) throw new Error(error.message);
    if (!data?.ok) throw new Error(data?.fout ?? 'Opslaan is niet gelukt.');
    return data as { submissionId: string; leadId?: string };
  };

  /**
   * Slaat de scan op. Geef `email` en `companyName` mee om in één keer ook de
   * contactgegevens vast te leggen; dan ontstaat meteen de lead.
   */
  const submitQuickscanData = async (
    data: QuickscanData,
    results: QuickscanResults,
    email?: string,
    companyName?: string,
  ): Promise<string> => {
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      const { submissionId } = await roep({ actie: 'opslaan', data, results });

      if (email && companyName) {
        await roep({ actie: 'contactgegevens', submissionId, email, companyName });
      }
      return submissionId;
    } catch (error) {
      const melding = error instanceof Error ? error.message : 'Er ging iets mis bij het opslaan van de data';
      console.error('Quickscan opslaan mislukt:', error);
      setSubmissionError(melding);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Vult een bestaande inzending aan met contactgegevens en maakt de lead.
   * Twee keer aanroepen levert één lead op; de functie is idempotent.
   */
  const saveContactDetails = async (
    submissionId: string,
    email: string,
    companyName: string,
  ): Promise<string | undefined> => {
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      const { leadId } = await roep({ actie: 'contactgegevens', submissionId, email, companyName });
      return leadId;
    } catch (error) {
      const melding = error instanceof Error ? error.message : 'Opslaan van je gegevens is niet gelukt';
      console.error('Contactgegevens opslaan mislukt:', error);
      setSubmissionError(melding);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitQuickscanData,
    saveContactDetails,
    isSubmitting,
    submissionError,
  };
};
